import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import { getStoredUser } from "../../utils/permissions";
import type {
  BankAccount,
  FinanceDepartment,
  FinanceParty,
  PurchaseDetail,
  StockItem,
} from "../../types";

const { Text } = Typography;

/**
 * Only these four reach the ledger: `qr` exists for POS tenders but has no
 * bank account to post against on an invoice, so it is not offered here.
 */
const PAYMENT_METHODS = [
  { value: "cash", label: "Cash", bankType: "cash" as const },
  { value: "bank_transfer", label: "Bank transfer", bankType: "bank" as const },
  { value: "card", label: "Card", bankType: "card" as const },
  { value: "credit", label: "On credit (pay later)", bankType: null },
];

/**
 * Sentinel option for a voucher raised before the supplier became a party
 * picker: those rows carry only the free-text name (`party`) and no `party_id`,
 * so the name is offered as the current selection instead of showing blank.
 */
const LEGACY_PARTY = "__legacy_party__";

interface DraftLine {
  key: string;
  stock_item_id?: string;
  quantity: number;
  unit_cost: number;
}

interface Props {
  open: boolean;
  /** When present the modal edits this draft / rejected invoice. */
  editing?: PurchaseDetail | null;
  onClose: () => void;
  onSaved: (saved: PurchaseDetail, created: boolean) => void;
}

const blankLine = (): DraftLine => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  quantity: 1,
  unit_cost: 0,
});

const round2 = (n: number) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const lineTotal = (l: DraftLine) => round2(Number(l.quantity || 0) * Number(l.unit_cost || 0));

/**
 * A purchase invoice, stock lines and all.
 *
 * The net amount is not a separate field the operator can drift away from: it
 * IS the sum of the lines, because the server refuses an invoice whose header
 * and lines disagree. Showing the total where the amount would otherwise sit
 * makes the rule visible instead of a validation error found after typing.
 *
 * The account is never offered. Goods go to Inventory (1200) because that is
 * what arrived — the server forces it regardless of what any client says.
 */
const PurchaseFormModal: React.FC<Props> = ({ open, editing, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<StockItem[]>([]);
  const [departments, setDepartments] = useState<FinanceDepartment[]>([]);
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [suppliers, setSuppliers] = useState<FinanceParty[]>([]);
  const [submitNow, setSubmitNow] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([]);

  const isEdit = !!editing;
  const method: string = Form.useWatch("payment_method", form) || "credit";
  const bankType = PAYMENT_METHODS.find((m) => m.value === method)?.bankType ?? null;

  /**
   * Ordered By is captured by the server from the authenticated user. This is
   * a read-only display of that snapshot: the original orderer on an edit, the
   * current login on a new purchase. It is deliberately not a form field, so
   * it is never submitted.
   */
  const orderedByName =
    editing?.ordered_by_name ||
    editing?.created_by_name ||
    getStoredUser()?.name ||
    "—";

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitNow(false);
    form.setFieldsValue({
      business_date: editing ? dayjs(editing.business_date) : dayjs(),
      department_id: editing?.department_id ?? undefined,
      party_id: editing?.party_id ?? undefined,
      reference_no: editing?.reference_no ?? "",
      description: editing?.description || "Stock purchase",
      tax: editing?.tax ?? 0,
      payment_method: editing?.payment_method ?? "credit",
      bank_account_id: editing?.bank_account_id ?? undefined,
      remarks: editing?.remarks ?? "",
    });
    setLines(
      editing?.lines?.length
        ? editing.lines.map((l, i) => ({
            key: `edit-${i}`,
            stock_item_id: l.stock_item_id,
            quantity: Number(l.quantity),
            unit_cost: Number(l.unit_cost),
          }))
        : [blankLine()],
    );
  }, [open, editing, form]);

  useEffect(() => {
    if (!open) return;
    financeService
      .getStockItems()
      .then(setItems)
      .catch(() => setItems([]));
    financeService
      .getDepartments()
      .then(setDepartments)
      .catch(() => setDepartments([]));
    financeService
      .getBankAccounts()
      .then((rows) => setBanks(rows.filter((b) => b.is_active)))
      .catch(() => setBanks([]));
    financeService
      .getParties("supplier")
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, [open]);

  const netTotal = useMemo(() => round2(lines.reduce((s, l) => s + lineTotal(l), 0)), [lines]);
  const tax = Number(Form.useWatch("tax", form) || 0);

  const updateLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const bankOptions = banks
    .filter((b) => b.type === bankType)
    .map((b) => ({ value: b.id, label: b.name }));

  const itemOptions = items.map((i) => ({
    value: i.id,
    label: `${i.code} — ${i.name} (${i.unit})`,
    disabled: !i.is_active,
  }));

  /**
   * Supplier picker options. Active suppliers are listed by name; the invoice's
   * own supplier is kept visible even when it is inactive or the voucher
   * predates `party_id` (name-only legacy rows use the LEGACY_PARTY sentinel).
   */
  const supplierOptions = useMemo(() => {
    const options = suppliers.map((s) => ({
      value: s.id,
      label: s.name,
      disabled: !s.is_active,
    }));
    if (editing?.party) {
      const current = editing.party_id ?? LEGACY_PARTY;
      if (!options.some((o) => o.value === current)) {
        options.unshift({ value: current, label: editing.party, disabled: false });
      }
    }
    return options;
  }, [suppliers, editing]);

  const handleSubmit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const badLine = lines.find(
      (l) => !l.stock_item_id || !(Number(l.quantity) > 0) || Number(l.unit_cost) < 0,
    );
    if (lines.length === 0) {
      setError("A purchase invoice needs at least one stock line.");
      return;
    }
    if (badLine) {
      setError("Every line needs an item, a quantity above zero and a unit cost of zero or more.");
      return;
    }

    // Resolve the picker back to both halves the voucher stores: the party id
    // for reporting links and the name snapshot for the ledger/printed row.
    const selectedPartyId =
      values.party_id === LEGACY_PARTY ? null : values.party_id ?? null;
    const selectedPartyName =
      values.party_id === LEGACY_PARTY
        ? editing?.party ?? null
        : suppliers.find((s) => s.id === values.party_id)?.name ??
          (editing && editing.party_id === values.party_id ? editing.party : null);

    const payload = {
      business_date: values.business_date.format("YYYY-MM-DD"),
      department_id: values.department_id,
      party: selectedPartyName,
      party_id: selectedPartyId,
      reference_no: values.reference_no ? String(values.reference_no).trim() : null,
      description: values.description,
      amount: netTotal,
      tax: Number(values.tax || 0),
      payment_method: values.payment_method,
      bank_account_id: bankType ? values.bank_account_id ?? null : null,
      remarks: values.remarks ? String(values.remarks).trim() : null,
      lines: lines.map((l) => ({
        // Narrowed by the checks above — a line without an item never gets
        // this far, so the server sees a complete document.
        stock_item_id: l.stock_item_id!,
        quantity: Number(l.quantity),
        unit_cost: Number(l.unit_cost),
      })),
      submit: submitNow,
    };

    setSaving(true);
    setError(null);
    try {
      const saved = editing
        ? ((await financeService.updatePurchase(editing.id, payload)) as PurchaseDetail)
        : ((await financeService.createPurchase(payload)) as PurchaseDetail);
      onSaved(saved, !isEdit);
    } catch (e: any) {
      setError(e?.message || "Failed to save purchase invoice");
    } finally {
      setSaving(false);
    }
  };

  const lineColumns = [
    {
      title: "Stock item",
      key: "item",
      render: (_: any, l: DraftLine) => (
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
          placeholder="Item received"
          value={l.stock_item_id}
          options={itemOptions}
          onChange={(v) => updateLine(l.key, { stock_item_id: v })}
        />
      ),
    },
    {
      title: "Qty",
      key: "quantity",
      width: 120,
      render: (_: any, l: DraftLine) => (
        <InputNumber
          min={0.01}
          step={1}
          style={{ width: "100%" }}
          value={l.quantity}
          onChange={(v) => updateLine(l.key, { quantity: Number(v ?? 0) })}
        />
      ),
    },
    {
      title: "Unit cost",
      key: "unit_cost",
      width: 150,
      render: (_: any, l: DraftLine) => (
        <InputNumber
          min={0}
          step={0.01}
          style={{ width: "100%" }}
          value={l.unit_cost}
          onChange={(v) => updateLine(l.key, { unit_cost: Number(v ?? 0) })}
        />
      ),
    },
    {
      title: "Line total",
      key: "line_total",
      width: 130,
      align: "right" as const,
      render: (_: any, l: DraftLine) => <Text strong>{formatLkr(lineTotal(l))}</Text>,
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_: any, l: DraftLine) => (
        <Button
          size="small"
          type="text"
          danger
          disabled={lines.length === 1}
          icon={<DeleteOutlined />}
          onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title={isEdit ? `Edit ${editing?.voucher_no}` : "New purchase invoice"}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={saving}
      okText={isEdit ? "Save changes" : submitNow ? "Create & submit" : "Create invoice"}
      width={860}
      destroyOnClose
    >
      {error && (
        <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />
      )}
      {items.length === 0 && (
        <Alert
          type="warning"
          showIcon
          title="No stock items available"
          description="An invoice can only receive items that exist in the cupboard. Add them on the Inventory page first — a purchase needs inventory:view to see them."
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col xs={24} sm={6}>
            <Form.Item
              name="business_date"
              label="Invoice date"
              rules={[{ required: true, message: "Required" }]}
            >
              <DatePicker style={{ width: "100%" }} allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name="department_id" label="Department">
              <Select
                allowClear
                placeholder="Purchases"
                options={departments.map((d) => ({ value: d.id, label: d.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name="reference_no" label="Supplier invoice no.">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              label="Ordered By"
              tooltip="Captured automatically from the logged-in user; saved when the order is created."
            >
              <Input value={orderedByName} disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="party_id"
              label="Supplier"
              rules={[
                {
                  validator: (_, v) =>
                    method === "credit" && !v
                      ? Promise.reject(new Error("A credit purchase needs a supplier"))
                      : Promise.resolve(),
                },
              ]}
            >
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                placeholder="Search supplier by name"
                options={supplierOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="description" label="Description" rules={[{ required: true }]}>
              <Input maxLength={500} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="tax" label="Tax (VAT input)" initialValue={0}>
              <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item name="payment_method" label="Paid by" initialValue="credit">
              <Select options={PAYMENT_METHODS} />
            </Form.Item>
          </Col>
          {bankType && (
            <Col xs={24} sm={8}>
              <Form.Item
                name="bank_account_id"
                label={`${bankType === "cash" ? "Cash" : bankType === "bank" ? "Bank" : "Card"} account`}
                rules={[{ required: true, message: "Required for this payment method" }]}
              >
                <Select placeholder="Where the money comes from" options={bankOptions} />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={8}>
            <Form.Item name="remarks" label="Internal remarks">
              <Input.TextArea rows={1} maxLength={500} />
            </Form.Item>
          </Col>
        </Row>

        <Table
          rowKey="key"
          size="small"
          columns={lineColumns}
          dataSource={lines}
          pagination={false}
          footer={() => (
            <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setLines((prev) => [...prev, blankLine()])}
              >
                Add line
              </Button>
              <Space size={24}>
                <Text type="secondary">
                  Net: <Text strong>{formatLkr(netTotal)}</Text>
                </Text>
                <Text type="secondary">
                  Total:{" "}
                  <Text strong>{formatLkr(round2(netTotal + Number(tax || 0)))}</Text>
                </Text>
              </Space>
            </Space>
          )}
        />
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
          The invoice net is the sum of these lines — that is what the receiving entries are
          measured against, so the two can never disagree.
        </Text>

        {!isEdit && (
          <Space style={{ marginTop: 16 }}>
            <Checkbox checked={submitNow} onChange={(e) => setSubmitNow(e.target.checked)}>
              Submit for approval straight away
            </Checkbox>
          </Space>
        )}
      </Form>
    </Modal>
  );
};

export default PurchaseFormModal;
